// A thin draggable divider. "vertical" sits between side-by-side regions (drag
// left/right); "horizontal" sits between stacked regions (drag up/down). It
// reports a pixel delta per move; the parent clamps + persists the size.

import { useCallback } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

interface Props {
  orientation: "vertical" | "horizontal";
  onResize: (deltaPx: number) => void;
  onResetDefault?: () => void;
}

export function Resizer({ orientation, onResize, onResetDefault }: Props) {
  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      let last = orientation === "vertical" ? e.clientX : e.clientY;
      const move = (ev: PointerEvent) => {
        const pos = orientation === "vertical" ? ev.clientX : ev.clientY;
        onResize(pos - last);
        last = pos;
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        document.body.classList.remove("resizing");
        document.body.style.cursor = "";
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      document.body.classList.add("resizing");
      document.body.style.cursor = orientation === "vertical" ? "col-resize" : "row-resize";
    },
    [orientation, onResize],
  );

  return (
    <div
      className={`resizer resizer-${orientation}`}
      onPointerDown={onPointerDown}
      onDoubleClick={onResetDefault}
      role="separator"
      aria-orientation={orientation === "vertical" ? "vertical" : "horizontal"}
      title="Drag to resize · double-click to reset"
    />
  );
}
